'use client';

import Image from 'next/image';
import { usePathname, useSearchParams } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { isBareRoute } from '@/lib/bare-routes';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import {
  ArrowLeftCircle,
  ChevronRight,
  HelpCircle,
  LayoutDashboard,
  PanelLeft,
  Search,
  SquarePen,
} from 'lucide-react';
import { useConnectorAccess } from '@/hooks/use-connector-access';
import { useConnectorFlowSnapshot } from '@/hooks/use-connector-flow-snapshot';
import { buildWorkspaceAwarePath } from '@/lib/active-workspace';
import {
  CONNECTOR_ADD_API_REVIEW_SCREEN,
  CONNECTOR_ADD_API_SCREEN,
  CONNECTOR_AUTH_SCREEN,
  CONNECTOR_COMPLETED_SCREEN,
  CONNECTOR_HOME_SCREEN,
  CONNECTOR_HOME_GUIDANCE_SCREEN,
  CONNECTOR_NEW_SCREEN,
  SPECS_ACTIONS_SCREEN,
  SPEC_ACTION_SCREEN,
} from '@/lib/connector-access';
import {
  type ConnectorFlowState,
  getFlowStateConnectorName,
  isPersistedSystemId,
} from '@/lib/connector-flow-state';
import { domId } from '@/lib/dom-id';
import { withLocalePath } from '@/lib/locale-routing';
import {
  countCompletedGuidanceSteps,
  findUnfinishedConnector,
  GUIDANCE_STEP_ORDER,
} from '@/lib/onboarding-guidance';
import { coerceLocale, type Locale } from '@/i18n/locales';
import { fetcher } from '@/lib/fetcher';
import { useMcps } from '@/services/hooks/use-mcps';
import { useSystems } from '@/services/hooks/use-systems';
import { useOnboardingProgress } from '@/services/hooks/use-onboarding';
import type { ActionRecord } from '@/services/action-service';
import type { ApiResponse } from '@/services/model';
import type { MCP } from '@/services/schemas/mcps-schemas';
import type { SystemRecord } from '@/services/system-service';
import { ConnectorIcon } from '@/components/icons/connector-icon';
import AddConnectorModal from '@/components/add-connector-modal';
import ChooseConnectionTypeModal from '@/components/choose-connection-type-modal';
import AddMcpModal from '@/components/mcp/add-mcp-modal';
import SidebarSearchModal, { type SidebarSearchItem } from '@/components/sidebar-search-modal';
import { getSystemLastUpdatedTimestamp } from '@/lib/system-last-updated';
import { useGlobalStore } from '@/store/global';
import { useSidebarStore } from '@/store/sidebar/sidebar-store';
import { useWorkspaceStore } from '@/store/workspace/workspace-store';
import type { OnboardingStep } from '@/types/onboarding';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import type { SWRConfiguration } from 'swr';

// Design tokens from Figma - now using Tailwind CSS variables
// These colors are defined in globals.css @theme section
const SIDEBAR_COLORS = {
  background: 'var(--color-sidebar-bg)',
  border: 'var(--color-sidebar-border)',
  text: 'var(--color-sidebar-text)',
  textMuted: 'var(--color-sidebar-text-muted)',
  textFooter: 'var(--color-sidebar-text-footer)',
  hoverBg: 'var(--color-sidebar-hover-bg)',
  activeBg: 'var(--color-sidebar-active-bg)',
  activeGradientStart: 'var(--color-sidebar-active-gradient-start)',
  activeGradientEnd: 'var(--color-sidebar-active-gradient-end)',
} as const;
const SIDEBAR_ICON_STROKE_WIDTH = 1.25;

// Shared component classes - using Tailwind where possible
const sidebarRailClass = 'flex w-full min-w-0 flex-col';
const sidebarSectionLabelClass =
  'text-xs font-medium leading-[16.8px] tracking-normal text-sidebar-text-muted';
const sidebarCompactItemClass =
  'flex h-8 w-full items-center gap-1 rounded-lg px-2 text-[13px] font-normal leading-[18.2px] text-sidebar-text transition-colors hover:bg-sidebar-hover-bg';
const sidebarNavItemClass =
  'flex h-8 w-full items-center gap-1 rounded-lg px-2 text-[13px] font-normal leading-[18.2px] text-sidebar-text transition-colors hover:bg-sidebar-hover-bg';
const sidebarSectionItemClass =
  'flex h-7 w-full items-center gap-1 rounded-lg px-2 text-[13px] font-normal leading-[18.2px] text-sidebar-text transition-colors hover:bg-sidebar-hover-bg';
const sidebarActiveItemClass = 'bg-sidebar-active-bg';
const sidebarIconOnlyItemClass =
  'flex size-8 items-center justify-center rounded-lg text-sidebar-text-muted transition-colors hover:bg-sidebar-hover-bg';
const sidebarCollapsedControlClass =
  'flex h-12 w-full items-center justify-center rounded-lg px-4 text-sidebar-text-muted transition-colors hover:bg-sidebar-hover-bg';
const sidebarCollapsedStackClass = 'flex w-full flex-col items-center gap-4';
const sidebarTopControlClass =
  'flex w-full items-center justify-between gap-1 rounded-lg pb-2 pl-2 text-xs font-normal leading-[16.8px] text-sidebar-text-muted';
const sidebarActiveSolidTextClass = 'font-medium text-sidebar-text-muted';
const sidebarQuietSwrOptions: SWRConfiguration = {
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  revalidateIfStale: false,
  dedupingInterval: 30_000,
};

// Helper function for active text gradient
const getActiveTextStyle = (isActive: boolean) => ({
  background: isActive
    ? `linear-gradient(90deg, ${SIDEBAR_COLORS.activeGradientStart} 0%, ${SIDEBAR_COLORS.activeGradientEnd} 140.38%)`
    : 'none',
  WebkitBackgroundClip: isActive ? 'text' : 'unset',
  WebkitTextFillColor: isActive ? 'transparent' : SIDEBAR_COLORS.text,
  backgroundClip: isActive ? 'text' : 'unset',
});

type SidebarGuidanceStepId = (typeof GUIDANCE_STEP_ORDER)[number];

function hasLocalApiDraft(flowState: ConnectorFlowState): boolean {
  return Boolean(
    flowState.previewActions.length > 0 || (flowState.hasAuthDraft && flowState.systemId)
  );
}

function createEffectiveGuidanceSteps(
  steps: OnboardingStep[] | null | undefined,
  localDraftState: {
    hasLocalConnectorDraft: boolean;
    hasLocalApiDraft: boolean;
    hasLocalAuthSetup: boolean;
  }
): OnboardingStep[] {
  const stepsById = new Map(steps?.map(step => [step.step_id, step]) ?? []);

  return GUIDANCE_STEP_ORDER.map(stepId => {
    const currentStep = stepsById.get(stepId);

    const localCompletionOverrides: Partial<Record<SidebarGuidanceStepId, boolean>> = {
      add_connector: localDraftState.hasLocalConnectorDraft,
      add_api: localDraftState.hasLocalApiDraft,
      configure_auth: localDraftState.hasLocalAuthSetup,
    };

    return {
      step_id: stepId,
      is_completed: Boolean(localCompletionOverrides[stepId]) || Boolean(currentStep?.is_completed),
      completed_at: currentStep?.completed_at,
    };
  });
}

function formatRelativeSidebarTime(value: number, locale: string): string {
  if (!Number.isFinite(value) || value <= 0) return '—';

  const diffMs = Date.now() - value;
  const diffSeconds = Math.round(diffMs / 1000);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  if (diffSeconds < 60) return rtf.format(-diffSeconds, 'second');
  const diffMins = Math.round(diffSeconds / 60);
  if (diffMins < 60) return rtf.format(-diffMins, 'minute');
  const diffHours = Math.round(diffMins / 60);
  if (diffHours < 24) return rtf.format(-diffHours, 'hour');
  const diffDays = Math.round(diffHours / 24);
  if (diffDays < 7) return rtf.format(-diffDays, 'day');

  return new Date(value).toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: diffDays > 365 ? 'numeric' : undefined,
  });
}

function buildSystemSearchItem(
  connector: Pick<
    SystemRecord,
    'system_id' | 'name' | 'description' | 'system_type' | 'last_updated_time' | 'last_updated'
  >,
  locale: Locale,
  fallbackLabel: string
): SidebarSearchItem {
  const updatedAt = getSystemLastUpdatedTimestamp(connector);
  const params = new URLSearchParams({
    system_id: connector.system_id,
  });
  if (connector.name) {
    params.set('name', connector.name);
  }
  if (connector.description) {
    params.set('description', connector.description);
  }

  return {
    id: `system-${connector.system_id}`,
    name: connector.name || fallbackLabel,
    description: connector.description || connector.system_type || '',
    href: buildWorkspaceAwarePath(withLocalePath(locale, '/specs/actions'), params),
    type: 'system',
    updatedLabel: formatRelativeSidebarTime(updatedAt, locale),
    updatedAt,
  };
}

function buildActionSearchItem(action: ActionRecord, locale: Locale): SidebarSearchItem {
  const timestamp = new Date(action.last_updated_time ?? 0).getTime();
  const updatedAt = Number.isFinite(timestamp) ? timestamp : 0;
  return {
    id: `action-${action.action_id}`,
    name: action.name,
    description: action.description,
    href: buildWorkspaceAwarePath(
      withLocalePath(locale, '/specs/action'),
      new URLSearchParams({ system_id: action.system_id, action_id: action.action_id })
    ),
    type: 'action',
    updatedLabel: formatRelativeSidebarTime(updatedAt, locale),
    updatedAt,
  };
}

function buildMcpSearchItem(
  server: Pick<MCP, 'mcp_id' | 'name' | 'description' | 'last_updated_time' | 'config'>,
  locale: Locale,
  fallbackLabel: string
): SidebarSearchItem {
  const updatedAt = new Date(server.last_updated_time ?? 0).getTime();
  return {
    id: `mcp-${server.mcp_id}`,
    name: server.name || fallbackLabel,
    description: server.description ?? server.config?.server_info?.description ?? '',
    href: buildWorkspaceAwarePath(withLocalePath(locale, `/mcp/${server.mcp_id}`)),
    type: 'mcp',
    updatedLabel: formatRelativeSidebarTime(updatedAt, locale),
    updatedAt,
  };
}

/**
 * Sidebar Component
 *
 * Left sidebar navigation with logo and main menu items
 * Features:
 * - Logo at the top
 * - New Connector and Search actions
 * - Quick Access section
 * - Connectors list
 * - Help Center footer
 * - Collapsible with toggle button
 */
export default function Sidebar() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const rawLocale = useLocale();
  const locale = coerceLocale(rawLocale);
  const t = useTranslations('Sidebar');
  const [isInplaceCopilot, bridgeAppId] = useGlobalStore(s => [
    s.bridge.isInplaceCopilot,
    s.bridge.appId,
  ]);
  const selectedWorkspace = useWorkspaceStore(state => state.selectedWorkspace);
  const { isCollapsed, sidebarWidth, setSidebarWidth, toggleCollapsed } = useSidebarStore();
  const [isResizing, setIsResizing] = useState(false);
  const flowState = useConnectorFlowSnapshot();
  const {
    canCreateSystem,
    canViewSystems,
    canViewActions,
    canViewMcps,
    canCreateMcp,
    canAccessScreen,
    isLoading: isConnectorAccessLoading,
  } = useConnectorAccess();
  const canOpenConnectorHome =
    (canViewSystems || canViewMcps) && canAccessScreen(CONNECTOR_HOME_SCREEN);
  const canOpenSystemDetails = canViewSystems && canAccessScreen(SPECS_ACTIONS_SCREEN);
  const canSearchActions = canViewActions && canAccessScreen(SPEC_ACTION_SCREEN);
  const normalizedPath = pathname?.replace(/^\/[a-z]{2}(?=\/|$)/, '') || '/';
  const localeRelativePath =
    normalizedPath !== '/' && normalizedPath.endsWith('/')
      ? normalizedPath.slice(0, -1)
      : normalizedPath || '/';
  const shouldTrackHomeOnboarding =
    localeRelativePath === '/' &&
    canCreateSystem &&
    canAccessScreen(CONNECTOR_HOME_GUIDANCE_SCREEN);
  const {
    onboardingProgress,
    isLoading: isOnboardingProgressLoading,
    isValidating: isOnboardingProgressValidating,
  } = useOnboardingProgress(shouldTrackHomeOnboarding);
  const {
    systems: systemsData,
    isLoading: isSystemsLoading,
    isValidating: isSystemsValidating,
  } = useSystems({
    enabled:
      canViewSystems && !isConnectorAccessLoading && !isBareRoute(localeRelativePath),
    swr: sidebarQuietSwrOptions,
  });
  const {
    mcps,
    isLoading: isMcpsLoading,
    isValidating: isMcpsValidating,
  } = useMcps({
    enabled: canViewMcps && !isConnectorAccessLoading && !isBareRoute(localeRelativePath),
    swr: sidebarQuietSwrOptions,
  });
  const connectors = useMemo<SystemRecord[]>(() => systemsData ?? [], [systemsData]);
  const mcpServers = useMemo(() => mcps ?? [], [mcps]);
  const isLoading = isSystemsLoading || isSystemsValidating;
  const isMcpLoading = isMcpsLoading || isMcpsValidating;
  const sortedConnectors = useMemo(
    () =>
      [...connectors].sort(
        (left, right) => getSystemLastUpdatedTimestamp(right) - getSystemLastUpdatedTimestamp(left)
      ),
    [connectors]
  );
  const sortedMcps = useMemo(
    () =>
      [...mcpServers].sort((left, right) => {
        const leftTs = new Date(left.last_updated_time ?? 0).getTime();
        const rightTs = new Date(right.last_updated_time ?? 0).getTime();
        return rightTs - leftTs;
      }),
    [mcpServers]
  );
  const searchItems = useMemo<SidebarSearchItem[]>(() => {
    const connectorItems = sortedConnectors.map(connector =>
      buildSystemSearchItem(connector, locale, t('quickAccess.allConnectors'))
    );

    const mcpItems = sortedMcps.map(server =>
      buildMcpSearchItem(server, locale, t('quickAccess.mcpServers'))
    );

    return [...connectorItems, ...mcpItems].sort((left, right) => right.updatedAt - left.updatedAt);
  }, [locale, sortedConnectors, sortedMcps, t]);
  const [recentSearchItems, setRecentSearchItems] = useState<SidebarSearchItem[]>([]);
  const [systemSectionOpen, setSystemSectionOpen] = useState(false);
  const [mcpSectionOpen, setMcpSectionOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [chooseConnectionTypeOpen, setChooseConnectionTypeOpen] = useState(false);
  const [addConnectorOpen, setAddConnectorOpen] = useState(false);
  const [addMcpOpen, setAddMcpOpen] = useState(false);

  useEffect(() => {
    if (recentSearchItems.length > 0) {
      return;
    }
    if (isLoading || isMcpLoading) {
      return;
    }
    if (searchItems.length === 0) {
      return;
    }
    setRecentSearchItems(searchItems.slice(0, 6));
  }, [isLoading, isMcpLoading, recentSearchItems.length, searchItems]);

  const handleSearch = useCallback(
    async (query: string): Promise<SidebarSearchItem[]> => {
      const body = JSON.stringify({
        search: query,
        limit: 1000,
        sort_field: {
          last_updated_time: -1,
        },
      });

      const [systemsResult, mcpsResult, actionsResult] = await Promise.allSettled([
        canViewSystems
          ? fetcher<ApiResponse<SystemRecord[]>>('/api/ai-transform/v1/system', {
              method: 'QUERY',
              body,
            })
          : Promise.resolve(undefined),
        canViewMcps
          ? fetcher<ApiResponse<MCP[]>>('/api/ai-transform/v1/mcp', {
              method: 'QUERY',
              body,
            })
          : Promise.resolve(undefined),
        canSearchActions
          ? fetcher<ApiResponse<ActionRecord[]>>('/api/ai-transform/v1/action', {
              method: 'QUERY',
              body,
            })
          : Promise.resolve(undefined),
      ]);

      const systemsResponse =
        systemsResult.status === 'fulfilled' ? systemsResult.value : undefined;
      const mcpsResponse = mcpsResult.status === 'fulfilled' ? mcpsResult.value : undefined;

      const systemItems = (systemsResponse?.data ?? []).map(connector =>
        buildSystemSearchItem(connector, locale, t('quickAccess.allConnectors'))
      );
      const mcpItems = (mcpsResponse?.data ?? []).map(server =>
        buildMcpSearchItem(server, locale, t('quickAccess.mcpServers'))
      );

      const actionItems =
        actionsResult.status === 'fulfilled'
          ? (actionsResult.value?.data ?? []).map(action => buildActionSearchItem(action, locale))
          : [];

      return [...systemItems, ...mcpItems, ...actionItems];
    },
    [canSearchActions, canViewMcps, canViewSystems, locale, t]
  );

  // Update CSS custom property for sidebar width.
  // On bare OAuth terminal routes (AD-10) zero the width so the marginLeft at
  // [locale]/layout.tsx:70 collapses; hiding the component alone leaves a
  // 200px dead gutter. On normal routes restore the live value.
  useEffect(() => {
    if (isBareRoute(localeRelativePath)) {
      document.documentElement.style.setProperty('--sidebar-width', '0px');
      return;
    }
    const width = isCollapsed ? '80px' : `${sidebarWidth}px`;
    document.documentElement.style.setProperty('--sidebar-width', width);
  }, [isCollapsed, localeRelativePath, sidebarWidth]);

  // Handle resize
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      // Cap at 90% of the viewport so the user can always grab the resize
      // handle to drag the sidebar back (at 100% the handle slides off-screen).
      const min = 180;
      const max = Math.round(window.innerWidth * 0.9);
      const next = Math.max(min, Math.min(max, e.clientX));
      setSidebarWidth(next);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, setSidebarWidth]);

  // Check if a path is active
  const isActive = (href: string) => {
    if (href === withLocalePath(locale, '/')) {
      // Homepage is active when we're on "/" regardless of workspace_id, but not when viewing a specific connector
      const isHomepage = pathname === href || pathname === `${href}/`;
      const hasSystemId = searchParams.get('system_id');
      return isHomepage && !hasSystemId;
    }
    return pathname?.startsWith(href);
  };

  // Check if a connector is active based on system_id query parameter
  const isConnectorActive = (systemId: string) => {
    const currentSystemId = searchParams.get('system_id');
    return currentSystemId === systemId;
  };

  const homeHref = buildWorkspaceAwarePath(withLocalePath(locale, '/'));
  const mcpHref = buildWorkspaceAwarePath(withLocalePath(locale, '/mcp'));
  const localDraftConnectorName = getFlowStateConnectorName(flowState);
  const hasLocalMcpIntegration = mcpServers.length > 0;
  const effectiveGuidanceSteps = createEffectiveGuidanceSteps(onboardingProgress?.steps, {
    hasLocalConnectorDraft: Boolean(localDraftConnectorName) || hasLocalMcpIntegration,
    hasLocalApiDraft: hasLocalApiDraft(flowState) || hasLocalMcpIntegration,
    hasLocalAuthSetup:
      Boolean(flowState.hasAuthDraft && flowState.systemId) || hasLocalMcpIntegration,
  });
  const isOnboardingCompleted =
    !shouldTrackHomeOnboarding ||
    countCompletedGuidanceSteps(effectiveGuidanceSteps) === GUIDANCE_STEP_ORDER.length;
  const currentConnectorScreen = `connector:${localeRelativePath}`;
  const isConnectorOnboardingRoute =
    currentConnectorScreen === CONNECTOR_NEW_SCREEN ||
    currentConnectorScreen === CONNECTOR_ADD_API_SCREEN ||
    currentConnectorScreen === CONNECTOR_ADD_API_REVIEW_SCREEN ||
    currentConnectorScreen === CONNECTOR_AUTH_SCREEN;
  const isCompletedRoute = currentConnectorScreen === CONNECTOR_COMPLETED_SCREEN;
  const isHomeOnboardingPending =
    localeRelativePath === '/' &&
    (isConnectorAccessLoading ||
      (shouldTrackHomeOnboarding &&
        (isOnboardingProgressLoading || isOnboardingProgressValidating)));
  const isHomeOnboarding =
    localeRelativePath === '/' &&
    !isHomeOnboardingPending &&
    shouldTrackHomeOnboarding &&
    !isOnboardingCompleted;
  const isOnboardingSidebarMode =
    isConnectorOnboardingRoute || isCompletedRoute || isHomeOnboarding;
  const completionConnectorName = searchParams.get('name')?.trim() || '';
  const completionSystemId = searchParams.get('system_id')?.trim() || '';
  const persistedFlowConnector = connectors.find(
    connector => connector.system_id === flowState.systemId
  );
  // Home Getting Started falls back to the same connector when this browser
  // holds no draft.
  const unfinishedConnector =
    isHomeOnboarding && !localDraftConnectorName && !flowState.systemId
      ? findUnfinishedConnector(sortedConnectors)
      : null;
  const flowConnector = persistedFlowConnector ?? unfinishedConnector;
  const completedConnector = connectors.find(
    connector => connector.system_id === completionSystemId
  );
  const onboardingConnectorName = isCompletedRoute
    ? completedConnector?.name?.trim() || completionConnectorName
    : localDraftConnectorName || flowConnector?.name?.trim() || '';
  const onboardingConnectorDescription = isCompletedRoute
    ? completedConnector?.description?.trim() || ''
    : flowConnector?.description?.trim() || '';
  const onboardingConnectorSystemId = isCompletedRoute
    ? completionSystemId
    : isPersistedSystemId(flowState.systemId)
      ? (flowState.systemId ?? '')
      : (unfinishedConnector?.system_id ?? '');
  const onboardingConnectorHref =
    canOpenSystemDetails && onboardingConnectorSystemId
      ? buildWorkspaceAwarePath(
          withLocalePath(locale, '/specs/actions'),
          (() => {
            const params = new URLSearchParams({
              system_id: onboardingConnectorSystemId,
            });
            if (onboardingConnectorName) {
              params.set('name', onboardingConnectorName);
            }
            if (onboardingConnectorDescription) {
              params.set('description', onboardingConnectorDescription);
            }
            return params;
          })()
        )
      : null;

  const handleOpenNewConnector = useCallback(() => {
    setChooseConnectionTypeOpen(true);
  }, []);

  const handleCloseConnectionType = useCallback(() => {
    setChooseConnectionTypeOpen(false);
  }, []);

  const handleSelectSystem = useCallback(() => {
    setChooseConnectionTypeOpen(false);
    setAddConnectorOpen(true);
  }, []);

  const handleSelectMcp = useCallback(() => {
    setChooseConnectionTypeOpen(false);
    setAddMcpOpen(true);
  }, []);

  const handleBackToCopilot = () => {
    if (typeof window === 'undefined') {
      return;
    }

    if (isInplaceCopilot) {
      window.parent.postMessage(
        {
          namespace: 'WS_BRIDGE',
          from: 'app',
          app_id: bridgeAppId,
          type: 'APP_NAVIGATE',
          version: '2.0',
          payload: { target: 'app' },
        },
        '*'
      );
      return;
    }

    const copilotUrl = process.env.NEXT_PUBLIC_COPILOT_URL;
    if (!copilotUrl) {
      console.warn('NEXT_PUBLIC_COPILOT_URL is not defined');
      return;
    }

    const url = new URL(copilotUrl);
    url.pathname = '/chat/';
    if (selectedWorkspace?.workspace_id) {
      url.searchParams.set('workspace_id', selectedWorkspace.workspace_id);
    }
    window.open(url.toString(), '_blank');
  };

  // Hide sidebar on bare OAuth terminal routes (AD-10); width already zeroed in the useEffect above.
  if (isBareRoute(localeRelativePath)) {
    return null;
  }

  return (
    <>
      <aside
        className={cn(
          'fixed left-0 bottom-0 z-30 flex flex-col items-center overflow-hidden bg-sidebar-bg',
          isCollapsed ? 'py-6' : 'py-3'
        )}
        style={{
          top: 'var(--app-header-offset, 57px)',
          width: isCollapsed ? '80px' : `${sidebarWidth}px`,
          transition: isResizing ? 'none' : 'width 300ms',
        }}
      >
        {/* Logo Section */}
        {/* <div className={`relative ${isCollapsed ? "px-2 pt-6 pb-4" : "px-6 pt-6 pb-4"}`}>
          {isCollapsed ? (
            <Link
              data-testid={domId("lnk", "sidebar-logo-icon")}
              href={withLocalePath(locale, "/")}
              className="flex justify-center hover:opacity-80 transition-opacity"
            >
              <OtenIcon size={40} />
            </Link>
          ) : (
            <Link
              data-testid={domId("lnk", "sidebar-logo")}
              href={withLocalePath(locale, "/")}
              className="block hover:opacity-80 transition-opacity"
            >
              <Image
                src="/otenlogo.svg"
                alt="Oten Connector"
                width={160}
                height={30}
                priority
                className="h-8 w-auto"
              />
            </Link>
          )}
        </div> */}

        {/* Main Navigation */}
        <nav
          className={cn(
            'flex flex-1 min-h-0 flex-col gap-4 py-0',
            isCollapsed ? 'items-center' : 'w-full items-center px-2'
          )}
        >
          {isOnboardingSidebarMode ? (
            <div
              className={cn(
                'flex flex-1 flex-col',
                isCollapsed ? 'items-center' : sidebarRailClass
              )}
            >
              {Boolean(onboardingConnectorName) &&
                (isCollapsed ? (
                  <div className="flex justify-center">
                    {onboardingConnectorHref ? (
                      <Link
                        data-testid={domId('lnk', 'sidebar-onboarding-connector')}
                        href={onboardingConnectorHref}
                        className={cn(sidebarIconOnlyItemClass, sidebarActiveItemClass)}
                        title={onboardingConnectorName}
                      >
                        <ConnectorIcon
                          className="flex-shrink-0"
                          size={20}
                          color={SIDEBAR_COLORS.textMuted}
                          strokeWidth={SIDEBAR_ICON_STROKE_WIDTH}
                        />
                      </Link>
                    ) : (
                      <div
                        className={cn(sidebarIconOnlyItemClass, sidebarActiveItemClass)}
                        title={onboardingConnectorName}
                      >
                        <ConnectorIcon
                          className="flex-shrink-0"
                          size={20}
                          color={SIDEBAR_COLORS.textMuted}
                          strokeWidth={SIDEBAR_ICON_STROKE_WIDTH}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col gap-0">
                    {onboardingConnectorHref ? (
                      <Button
                        data-testid={domId('btn', 'sidebar-onboarding-connector')}
                        variant="unstyled"
                        asChild
                        className={cn(sidebarNavItemClass, sidebarActiveItemClass, 'truncate')}
                      >
                        <Link
                          data-testid={domId('lnk', 'sidebar-onboarding-connector')}
                          href={onboardingConnectorHref}
                          title={onboardingConnectorName}
                        >
                          <ConnectorIcon
                            className="flex-shrink-0"
                            size={20}
                            color={SIDEBAR_COLORS.textMuted}
                            strokeWidth={SIDEBAR_ICON_STROKE_WIDTH}
                          />
                          <span className="truncate flex-1" style={getActiveTextStyle(true)}>
                            {onboardingConnectorName}
                          </span>
                        </Link>
                      </Button>
                    ) : (
                      <div
                        className={cn(sidebarNavItemClass, sidebarActiveItemClass, 'truncate')}
                        title={onboardingConnectorName}
                      >
                        <ConnectorIcon
                          className="flex-shrink-0"
                          size={20}
                          color={SIDEBAR_COLORS.textMuted}
                          strokeWidth={SIDEBAR_ICON_STROKE_WIDTH}
                        />
                        <span className="truncate flex-1" style={getActiveTextStyle(true)}>
                          {onboardingConnectorName}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          ) : isCollapsed ? (
            // Collapsed view - only icons
            <div className="flex w-full flex-1 flex-col justify-between items-center">
              <div className={sidebarCollapsedStackClass}>
                <Button
                  type="button"
                  variant="unstyled"
                  onClick={toggleCollapsed}
                  data-testid={domId('btn', 'sidebar-expand')}
                  className={sidebarCollapsedControlClass}
                  title={t('actions.expand')}
                  aria-label={t('actions.expand')}
                >
                  <PanelLeft
                    className="h-6 w-6 flex-shrink-0"
                    strokeWidth={SIDEBAR_ICON_STROKE_WIDTH}
                  />
                </Button>

                <Button
                  type="button"
                  variant="unstyled"
                  onClick={handleBackToCopilot}
                  data-testid={domId('btn', 'sidebar-back-to-copilot-collapsed')}
                  className={sidebarCollapsedControlClass}
                  title={t('actions.backToCopilot')}
                  aria-label={t('actions.backToCopilot')}
                >
                  <Image
                    src="/sidebar/copilot_icon.svg"
                    alt=""
                    width={24}
                    height={24}
                    aria-hidden="true"
                    className="shrink-0"
                  />
                </Button>

                {(canCreateSystem || canCreateMcp) && (
                  <Button
                    data-testid={domId('btn', 'sidebar-new-connector-collapsed')}
                    variant="unstyled"
                    className={sidebarCollapsedControlClass}
                    type="button"
                    onClick={handleOpenNewConnector}
                    title={t('actions.newConnector')}
                    aria-label={t('actions.newConnector')}
                  >
                    <SquarePen
                      className="h-6 w-6 flex-shrink-0"
                      strokeWidth={SIDEBAR_ICON_STROKE_WIDTH}
                    />
                  </Button>
                )}

                {canOpenConnectorHome && (
                  <Link
                    data-testid={domId('lnk', 'sidebar-all-connectors')}
                    href={homeHref}
                    className={cn(
                      sidebarCollapsedControlClass,
                      isActive(withLocalePath(locale, '/'))
                        ? sidebarActiveItemClass
                        : 'bg-transparent'
                    )}
                    title={t('quickAccess.allConnectors')}
                    aria-label={t('quickAccess.allConnectors')}
                  >
                    <LayoutDashboard
                      className="h-6 w-6 flex-shrink-0"
                      strokeWidth={SIDEBAR_ICON_STROKE_WIDTH}
                    />
                  </Link>
                )}

                {canViewMcps && (
                  <Link
                    data-testid={domId('lnk', 'sidebar-mcp-servers-collapsed')}
                    href={mcpHref}
                    className={cn(
                      sidebarCollapsedControlClass,
                      isActive(withLocalePath(locale, '/mcp'))
                        ? sidebarActiveItemClass
                        : 'bg-transparent'
                    )}
                    title={t('quickAccess.mcpServers')}
                    aria-label={t('quickAccess.mcpServers')}
                  >
                    <Image
                      src="/sidebar/mcp_icon.svg"
                      alt=""
                      width={24}
                      height={24}
                      aria-hidden="true"
                      className="shrink-0"
                    />
                  </Link>
                )}
              </div>
            </div>
          ) : (
            // Expanded view - full content
            <>
              <div className={cn(sidebarRailClass, 'min-h-0 flex-1')}>
                <div className={sidebarTopControlClass}>
                  <Button
                    type="button"
                    variant="unstyled"
                    onClick={handleBackToCopilot}
                    data-testid={domId('btn', 'sidebar-back-to-copilot')}
                    className="flex items-center gap-1 rounded-lg text-sidebar-text-muted hover:text-sidebar-text"
                  >
                    <ArrowLeftCircle
                      className="h-4 w-4 flex-shrink-0"
                      strokeWidth={SIDEBAR_ICON_STROKE_WIDTH}
                    />
                    <span>{t('actions.backToCopilot')}</span>
                  </Button>
                  <Button
                    type="button"
                    variant="unstyled"
                    onClick={toggleCollapsed}
                    data-testid={domId('btn', 'sidebar-collapse')}
                    className="flex size-5 items-center justify-center rounded-md text-sidebar-text-muted hover:bg-sidebar-hover-bg hover:text-sidebar-text"
                    title={t('actions.collapse')}
                    aria-label={t('actions.collapse')}
                  >
                    <PanelLeft
                      className="h-5 w-5 flex-shrink-0"
                      strokeWidth={SIDEBAR_ICON_STROKE_WIDTH}
                    />
                  </Button>
                </div>

                {/* Top Section - New Connector & Search */}
                <div className="flex flex-col gap-0">
                  {/* New Connector Button - Only show if user has access */}
                  {(canCreateSystem || canCreateMcp) && (
                    <Button
                      data-testid={domId('btn', 'sidebar-new-connector')}
                      variant="unstyled"
                      className={sidebarCompactItemClass}
                      type="button"
                      onClick={handleOpenNewConnector}
                    >
                      <SquarePen
                        className="h-5 w-5 flex-shrink-0"
                        strokeWidth={SIDEBAR_ICON_STROKE_WIDTH}
                      />
                      <span className="flex-1 text-left">{t('actions.newConnector')}</span>
                    </Button>
                  )}

                  <Button
                    type="button"
                    variant="unstyled"
                    data-testid={domId('btn', 'sidebar-search')}
                    className={sidebarCompactItemClass}
                    onClick={() => setSearchOpen(true)}
                  >
                    <Search
                      className="h-5 w-5 flex-shrink-0"
                      strokeWidth={SIDEBAR_ICON_STROKE_WIDTH}
                    />
                    <span className="flex-1 text-left">{t('actions.search')}</span>
                  </Button>
                </div>

                {/* Quick Access Section */}
                {canOpenConnectorHome && (
                  <div className="mt-4 flex flex-col gap-2">
                    <h3 className={cn(sidebarSectionLabelClass, 'px-2')}>
                      {t('sections.quickAccess')}
                    </h3>
                    <Button
                      data-testid={domId('btn', 'sidebar-all-connectors')}
                      variant="unstyled"
                      asChild
                      className={cn(
                        sidebarNavItemClass,
                        isActive(withLocalePath(locale, '/'))
                          ? sidebarActiveItemClass
                          : 'bg-transparent'
                      )}
                    >
                      <Link data-testid={domId('lnk', 'sidebar-all-connectors')} href={homeHref}>
                        <LayoutDashboard
                          className={cn(
                            'h-5 w-5 flex-shrink-0',
                            isActive(withLocalePath(locale, '/'))
                              ? 'text-sidebar-text-muted'
                              : 'text-sidebar-text-muted'
                          )}
                          strokeWidth={SIDEBAR_ICON_STROKE_WIDTH}
                        />
                        <span
                          className={cn(
                            'flex-1 text-left',
                            isActive(withLocalePath(locale, '/')) ? sidebarActiveSolidTextClass : ''
                          )}
                          style={
                            isActive(withLocalePath(locale, '/'))
                              ? undefined
                              : getActiveTextStyle(false)
                          }
                        >
                          {t('quickAccess.allConnectors')}
                        </span>
                      </Link>
                    </Button>
                    {/* MCP Servers */}
                    {/* <Button
                    data-testid={domId("btn", "sidebar-mcp-servers")}
                    variant="unstyled"
                    asChild
                    className={cn(
                      sidebarNavItemClass,
                      isActive(withLocalePath(locale, "/mcp"))
                        ? sidebarActiveItemClass
                        : "bg-transparent",
                    )}
                  >
                    <Link
                      data-testid={domId("lnk", "sidebar-mcp-servers")}
                      href={mcpHref}
                    >
                      <Image
                        src="/sidebar/mcp_icon.svg"
                        alt=""
                        width={20}
                        height={20}
                        aria-hidden="true"
                        className="shrink-0"
                      />
                      <span
                        className={cn(
                          "flex-1 text-left",
                          isActive(withLocalePath(locale, "/mcp"))
                            ? sidebarActiveSolidTextClass
                            : "",
                        )}
                        style={
                          isActive(withLocalePath(locale, "/mcp"))
                            ? undefined
                            : getActiveTextStyle(false)
                        }
                      >
                        {t("quickAccess.mcpServers")}
                      </span>
                    </Link>
                  </Button> */}
                  </div>
                )}

                {/* Connector Lists */}
                <div className="mt-4 flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
                  <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
                    {canOpenSystemDetails && (
                      <Collapsible
                        open={systemSectionOpen}
                        onOpenChange={setSystemSectionOpen}
                        className="flex flex-col"
                      >
                        <div className="flex items-center gap-0.5">
                          <CollapsibleTrigger asChild>
                            <button
                              type="button"
                              className="inline-flex min-w-0 flex-1 items-center gap-0.5 text-left"
                            >
                              <ChevronRight
                                className={cn(
                                  'h-4 w-4 shrink-0 transition-transform',
                                  systemSectionOpen ? 'rotate-90' : ''
                                )}
                                strokeWidth={SIDEBAR_ICON_STROKE_WIDTH}
                              />
                              <span className={sidebarSectionLabelClass}>
                                {t('sections.connectors')}
                              </span>
                            </button>
                          </CollapsibleTrigger>
                          <span className="inline-flex min-w-6 items-center justify-center rounded-[10px] bg-gray-200 px-2 text-xs font-normal leading-[18px] text-gray-400">
                            {sortedConnectors.length}
                          </span>
                        </div>
                        <CollapsibleContent className="overflow-hidden data-[state=open]:[animation:collapsible-down_200ms_ease-out] data-[state=closed]:[animation:collapsible-up_150ms_ease-in]">
                          <ul className="flex flex-col gap-0">
                            {isLoading ? (
                              <li className="px-2 py-2 text-xs leading-4 text-sidebar-text-muted">
                                {t('loading')}
                              </li>
                            ) : sortedConnectors.length === 0 ? (
                              <li className="px-2 py-2 text-xs leading-4 text-sidebar-text-muted">
                                {t('noConnectors')}
                              </li>
                            ) : (
                              sortedConnectors.map(connector => {
                                const isActive = isConnectorActive(connector.system_id);
                                return (
                                  <li key={connector.system_id}>
                                    <Button
                                      data-testid={domId(
                                        'btn',
                                        `sidebar-connector-${connector.system_id}`
                                      )}
                                      variant="unstyled"
                                      asChild
                                      className={cn(
                                        sidebarSectionItemClass,
                                        'truncate',
                                        isActive ? sidebarActiveItemClass : 'bg-transparent'
                                      )}
                                    >
                                      <Link
                                        data-testid={domId(
                                          'lnk',
                                          `sidebar-connector-${connector.system_id}`
                                        )}
                                        href={buildWorkspaceAwarePath(
                                          withLocalePath(locale, '/specs/actions'),
                                          (() => {
                                            const params = new URLSearchParams({
                                              system_id: connector.system_id,
                                            });
                                            if (connector.name) {
                                              params.set('name', connector.name);
                                            }
                                            if (connector.description) {
                                              params.set('description', connector.description);
                                            }
                                            return params;
                                          })()
                                        )}
                                        title={connector.name}
                                      >
                                        <ConnectorIcon
                                          className="flex-shrink-0"
                                          size={20}
                                          color={SIDEBAR_COLORS.textMuted}
                                          strokeWidth={SIDEBAR_ICON_STROKE_WIDTH}
                                        />
                                        <span
                                          className="truncate flex-1 text-[13px] leading-[18.2px]"
                                          style={getActiveTextStyle(isActive)}
                                        >
                                          {connector.name}
                                        </span>
                                      </Link>
                                    </Button>
                                  </li>
                                );
                              })
                            )}
                          </ul>
                        </CollapsibleContent>
                      </Collapsible>
                    )}

                    {canViewMcps && (
                      <Collapsible
                        open={mcpSectionOpen}
                        onOpenChange={setMcpSectionOpen}
                        className="flex flex-col"
                      >
                      <div className="flex items-center gap-0.5">
                        <CollapsibleTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex min-w-0 flex-1 items-center gap-0.5 text-left"
                          >
                            <ChevronRight
                              className={cn(
                                'h-4 w-4 shrink-0 transition-transform',
                                mcpSectionOpen ? 'rotate-90' : ''
                              )}
                              strokeWidth={SIDEBAR_ICON_STROKE_WIDTH}
                            />
                            <span className={sidebarSectionLabelClass}>
                              {t('sections.mcpServers')}
                            </span>
                          </button>
                        </CollapsibleTrigger>
                        <span className="inline-flex min-w-6 items-center justify-center rounded-[10px] bg-gray-200 px-2 text-xs font-normal leading-[18px] text-gray-400">
                          {sortedMcps.length}
                        </span>
                      </div>
                      <CollapsibleContent className="overflow-hidden data-[state=open]:[animation:collapsible-down_200ms_ease-out] data-[state=closed]:[animation:collapsible-up_150ms_ease-in]">
                        <ul className="flex flex-col gap-0">
                          {isMcpLoading ? (
                            <li className="px-2 py-2 text-xs leading-4 text-sidebar-text-muted">
                              {t('loading')}
                            </li>
                          ) : sortedMcps.length === 0 ? (
                            <li className="px-2 py-2 text-xs leading-4 text-sidebar-text-muted">
                              {t('noMcpServers')}
                            </li>
                          ) : (
                            sortedMcps.map(server => {
                              const serverHref = buildWorkspaceAwarePath(
                                withLocalePath(locale, `/mcp/${server.mcp_id}`)
                              );
                              const isActive = pathname?.startsWith(serverHref);
                              return (
                                <li key={server.mcp_id}>
                                  <Button
                                    data-testid={domId('btn', `sidebar-mcp-${server.mcp_id}`)}
                                    variant="unstyled"
                                    asChild
                                    className={cn(
                                      sidebarSectionItemClass,
                                      'truncate',
                                      isActive ? sidebarActiveItemClass : 'bg-transparent'
                                    )}
                                  >
                                    <Link
                                      data-testid={domId('lnk', `sidebar-mcp-${server.mcp_id}`)}
                                      href={serverHref}
                                      title={server.name ?? t('quickAccess.mcpServers')}
                                    >
                                      <Image
                                        src="/sidebar/mcp_icon.svg"
                                        alt=""
                                        width={20}
                                        height={20}
                                        aria-hidden="true"
                                        className="shrink-0"
                                      />
                                      <span
                                        className="truncate flex-1 text-[13px] leading-[18.2px]"
                                        style={getActiveTextStyle(Boolean(isActive))}
                                      >
                                        {server.name ?? t('quickAccess.mcpServers')}
                                      </span>
                                    </Link>
                                  </Button>
                                </li>
                              );
                            })
                          )}
                        </ul>
                      </CollapsibleContent>
                      </Collapsible>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </nav>

        {/* Footer */}
        <div
          className={cn(
            'mt-auto flex flex-col gap-2',
            isCollapsed ? 'items-center' : 'w-full items-start px-2'
          )}
        >
          {isCollapsed ? (
            <a
              data-testid={domId('lnk', 'sidebar-help-center')}
              href="https://docs.oten.com/oten-ai-support/using-oten-connectors-system-integration/set-up-your-first-system-connector"
              target="_blank"
              rel="noopener noreferrer"
              className="flex size-4 items-center justify-center text-sidebar-text-muted transition-colors hover:text-sidebar-text"
              title={t('footer.helpCenter')}
            >
              <HelpCircle
                className="h-4 w-4 flex-shrink-0"
                strokeWidth={SIDEBAR_ICON_STROKE_WIDTH}
              />
            </a>
          ) : (
            <>
              <a
                data-testid={domId('lnk', 'sidebar-help-center')}
                href="https://docs.oten.com/oten-ai-support/using-oten-connectors-system-integration/set-up-your-first-system-connector"
                target="_blank"
                rel="noopener noreferrer"
                className="flex w-full min-w-0 items-center gap-1 text-xs font-normal leading-4 text-sidebar-text-muted"
              >
                <HelpCircle
                  className="h-4 w-4 flex-shrink-0"
                  strokeWidth={SIDEBAR_ICON_STROKE_WIDTH}
                />
                <span className="flex-1 truncate">{t('footer.helpCenter')}</span>
              </a>
              <p className="w-full min-w-0 text-xs leading-4 text-sidebar-text-footer">
                {t('footer.version')}
              </p>
            </>
          )}
        </div>

        {/* Resize Handle */}
        {!isCollapsed && (
          <div
            data-testid={domId('div', 'sidebar-resize-handle')}
            className="absolute top-0 right-0 bottom-0 w-1 cursor-col-resize hover:bg-purple-400 transition-colors group"
            onMouseDown={() => setIsResizing(true)}
          >
            <div className="absolute top-0 right-0 bottom-0 w-4 -mr-2" />
          </div>
        )}
      </aside>

      <SidebarSearchModal
        open={searchOpen}
        onOpenChange={setSearchOpen}
        items={recentSearchItems}
        onSearch={handleSearch}
      />
      {(canCreateSystem || canCreateMcp) && (
        <ChooseConnectionTypeModal
          open={chooseConnectionTypeOpen}
          onClose={handleCloseConnectionType}
          onSelectSystem={handleSelectSystem}
          onSelectMcp={handleSelectMcp}
          allowSystem={canCreateSystem}
          allowMcp={canCreateMcp}
        />
      )}
      {canCreateSystem && (
        <AddConnectorModal open={addConnectorOpen} onOpenChange={setAddConnectorOpen} />
      )}
      {canCreateMcp && (
        <AddMcpModal
          open={addMcpOpen}
          onClose={() => setAddMcpOpen(false)}
          onCreated={() => setAddMcpOpen(false)}
        />
      )}
    </>
  );
}
